import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, ShoppingBag, Star, Clock, MapPin, IndianRupee,
  CheckCircle2, Plus, Package, Briefcase, FileText, Shield,
  Building2, Calculator, Users, Landmark, Filter, X,
  TrendingUp, BarChart3, Zap,
} from "lucide-react";

const Card = ({ children, className = "", onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`} onClick={onClick}>{children}</div>
);

const CATEGORIES = [
  { value: "accounting", label: "Accounting Services", icon: Calculator, color: "text-accent" },
  { value: "gst", label: "GST Filing", icon: FileText, color: "text-success" },
  { value: "tax", label: "Tax Consultation", icon: Landmark, color: "text-primary" },
  { value: "registration", label: "Company Registration", icon: Building2, color: "text-warning" },
  { value: "audit", label: "Audit Services", icon: Shield, color: "text-destructive" },
  { value: "payroll", label: "Payroll Management", icon: Users, color: "text-accent" },
  { value: "advisory", label: "Business Advisory", icon: Briefcase, color: "text-success" },
  { value: "compliance", label: "Compliance", icon: CheckCircle2, color: "text-primary" },
];

const CITIES = [
  "All Cities", "Mumbai", "Delhi", "Bangalore", "Chennai", "Hyderabad",
  "Pune", "Kolkata", "Ahmedabad", "Jaipur", "Lucknow",
];

type ServiceListing = {
  id: string;
  provider_user_id: string;
  provider_name: string;
  provider_type: string;
  title: string;
  category: string;
  description: string | null;
  features: string[];
  price_type: string;
  price_amount: number;
  price_label: string | null;
  delivery_time: string | null;
  city: string | null;
  is_active: boolean;
  rating: number;
  total_orders: number;
  created_at: string;
};

type ServiceOrder = {
  id: string;
  listing_id: string;
  buyer_user_id: string;
  buyer_name: string;
  buyer_company: string | null;
  buyer_email: string;
  status: string;
  amount: number;
  payment_status: string;
  created_at: string;
};

export default function AccouProMarketplacePage() {
  const { user, role } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isProvider = role === "ca" || role === "super_admin";

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("All Cities");
  const [selectedListing, setSelectedListing] = useState<ServiceListing | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("browse");

  // Create listing form
  const [listingForm, setListingForm] = useState({
    title: "", category: "accounting", description: "", features: "",
    price_type: "fixed", price_amount: "", price_label: "",
    delivery_time: "", city: "",
  });

  // Order form
  const [orderForm, setOrderForm] = useState({
    buyer_name: "", buyer_company: "", buyer_email: "", buyer_phone: "", requirements: "",
  });

  // Fetch listings
  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["service-listings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_listings")
        .select("*")
        .eq("is_active", true)
        .order("total_orders", { ascending: false });
      if (error) throw error;
      return data as ServiceListing[];
    },
  });

  // Fetch my orders
  const { data: myOrders = [] } = useQuery({
    queryKey: ["service-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_orders")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as ServiceOrder[];
    },
  });

  // Create listing mutation
  const createListing = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("service_listings").insert({
        provider_user_id: user!.id,
        provider_name: user?.email?.split("@")[0] || "Provider",
        provider_type: role || "ca",
        title: listingForm.title,
        category: listingForm.category,
        description: listingForm.description || null,
        features: listingForm.features.split(",").map((f) => f.trim()).filter(Boolean),
        price_type: listingForm.price_type,
        price_amount: Number(listingForm.price_amount) || 0,
        price_label: listingForm.price_label || null,
        delivery_time: listingForm.delivery_time || null,
        city: listingForm.city || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-listings"] });
      setShowCreateDialog(false);
      setListingForm({ title: "", category: "accounting", description: "", features: "", price_type: "fixed", price_amount: "", price_label: "", delivery_time: "", city: "" });
      toast({ title: "Service listed!", description: "Your service is now visible in the marketplace." });
    },
    onError: () => toast({ title: "Error", description: "Failed to create listing.", variant: "destructive" }),
  });

  // Place order mutation
  const placeOrder = useMutation({
    mutationFn: async () => {
      if (!selectedListing) return;
      const { error } = await supabase.from("service_orders").insert({
        listing_id: selectedListing.id,
        buyer_user_id: user!.id,
        buyer_name: orderForm.buyer_name,
        buyer_company: orderForm.buyer_company || null,
        buyer_email: orderForm.buyer_email,
        buyer_phone: orderForm.buyer_phone || null,
        requirements: orderForm.requirements || null,
        amount: selectedListing.price_amount,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-orders"] });
      setShowOrderDialog(false);
      setSelectedListing(null);
      setOrderForm({ buyer_name: "", buyer_company: "", buyer_email: "", buyer_phone: "", requirements: "" });
      toast({ title: "Order placed!", description: "The service provider will be notified." });
    },
    onError: () => toast({ title: "Error", description: "Failed to place order.", variant: "destructive" }),
  });

  // Filtering
  const filtered = listings.filter((l) => {
    if (categoryFilter !== "all" && l.category !== categoryFilter) return false;
    if (cityFilter !== "All Cities" && l.city !== cityFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return l.title.toLowerCase().includes(q) || l.description?.toLowerCase().includes(q) || l.provider_name.toLowerCase().includes(q);
    }
    return true;
  });

  const catCfg = (cat: string) => CATEGORIES.find((c) => c.value === cat) || CATEGORIES[0];

  // Stats
  const stats = {
    totalListings: listings.length,
    totalOrders: myOrders.length,
    pendingOrders: myOrders.filter((o) => o.status === "pending").length,
    categories: new Set(listings.map((l) => l.category)).size,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">AccouPro Marketplace</h1>
          <p className="text-sm text-muted-foreground">Discover and purchase professional accounting & compliance services</p>
        </div>
        {isProvider && (
          <Button onClick={() => setShowCreateDialog(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" /> List a Service
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Services Listed", value: stats.totalListings, icon: ShoppingBag, color: "text-accent" },
          { label: "Categories", value: stats.categories, icon: Package, color: "text-success" },
          { label: "My Orders", value: stats.totalOrders, icon: BarChart3, color: "text-primary" },
          { label: "Pending", value: stats.pendingOrders, icon: Clock, color: "text-warning" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-muted-foreground">{s.label}</span>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div className="text-xl font-bold font-display text-foreground">{s.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Category Pills */}
      <div className="flex gap-2 flex-wrap">
        <Badge
          variant={categoryFilter === "all" ? "default" : "outline"}
          className="cursor-pointer px-3 py-1.5"
          onClick={() => setCategoryFilter("all")}
        >
          All Services
        </Badge>
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <Badge
              key={cat.value}
              variant={categoryFilter === cat.value ? "default" : "outline"}
              className="cursor-pointer px-3 py-1.5 flex items-center gap-1.5"
              onClick={() => setCategoryFilter(cat.value)}
            >
              <Icon className="h-3 w-3" /> {cat.label}
            </Badge>
          );
        })}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="browse">Browse Services</TabsTrigger>
          <TabsTrigger value="orders">My Orders ({myOrders.length})</TabsTrigger>
          {isProvider && <TabsTrigger value="my-listings">My Listings</TabsTrigger>}
        </TabsList>

        {/* Browse Tab */}
        <TabsContent value="browse" className="space-y-4 mt-4">
          {/* Search & Filter */}
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search services..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-[160px]">
                <MapPin className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Listings Grid */}
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-10">Loading marketplace...</p>
          ) : filtered.length === 0 ? (
            <Card className="text-center py-12">
              <ShoppingBag className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No services found matching your filters.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <AnimatePresence>
                {filtered.map((listing, i) => {
                  const cat = catCfg(listing.category);
                  const CatIcon = cat.icon;
                  return (
                    <motion.div
                      key={listing.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <Card className="hover:border-accent/40 transition-colors cursor-pointer group"
                        onClick={() => { setSelectedListing(listing); setShowOrderDialog(true); }}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                            <CatIcon className={`h-5 w-5 ${cat.color}`} />
                          </div>
                          <Badge variant="outline" className="text-[10px]">{cat.label}</Badge>
                        </div>
                        <h4 className="text-sm font-semibold text-foreground group-hover:text-accent transition-colors line-clamp-1">
                          {listing.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{listing.description || "Professional service"}</p>

                        {listing.features.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {listing.features.slice(0, 3).map((f, j) => (
                              <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/30 text-muted-foreground">{f}</span>
                            ))}
                            {listing.features.length > 3 && (
                              <span className="text-[10px] text-muted-foreground">+{listing.features.length - 3}</span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Star className="h-3 w-3 text-warning fill-warning" /> {listing.rating.toFixed(1)}
                            </span>
                            <span className="flex items-center gap-1">
                              <ShoppingBag className="h-3 w-3" /> {listing.total_orders} orders
                            </span>
                            {listing.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {listing.city}
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-bold text-foreground flex items-center">
                            <IndianRupee className="h-3.5 w-3.5" />
                            {listing.price_amount.toLocaleString("en-IN")}
                            {listing.price_label && <span className="text-[10px] font-normal text-muted-foreground ml-0.5">/{listing.price_label}</span>}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{listing.provider_name}</span>
                          {listing.delivery_time && (
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {listing.delivery_time}</span>
                          )}
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </TabsContent>

        {/* My Orders Tab */}
        <TabsContent value="orders" className="space-y-3 mt-4">
          {myOrders.length === 0 ? (
            <Card className="text-center py-10">
              <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No orders yet.</p>
            </Card>
          ) : (
            myOrders.map((order) => (
              <Card key={order.id}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{order.buyer_company || order.buyer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      Ordered {new Date(order.created_at).toLocaleDateString("en-IN")} · ₹{order.amount.toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={
                      order.status === "completed" ? "bg-success/10 text-success border-success/20"
                      : order.status === "in_progress" ? "bg-accent/10 text-accent border-accent/20"
                      : "bg-muted/20 text-muted-foreground border-border"
                    }>
                      {order.status}
                    </Badge>
                    <Badge variant="outline" className={
                      order.payment_status === "paid" ? "bg-success/10 text-success border-success/20"
                      : "bg-warning/10 text-warning border-warning/20"
                    }>
                      {order.payment_status}
                    </Badge>
                  </div>
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        {/* My Listings Tab (providers) */}
        {isProvider && (
          <TabsContent value="my-listings" className="space-y-3 mt-4">
            {listings.filter((l) => l.provider_user_id === user?.id).length === 0 ? (
              <Card className="text-center py-10">
                <Briefcase className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">You haven't listed any services yet.</p>
                <Button size="sm" className="mt-3" onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Create Listing
                </Button>
              </Card>
            ) : (
              listings.filter((l) => l.provider_user_id === user?.id).map((listing) => {
                const cat = catCfg(listing.category);
                const CatIcon = cat.icon;
                return (
                  <Card key={listing.id}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                          <CatIcon className={`h-4 w-4 ${cat.color}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{listing.title}</p>
                          <p className="text-xs text-muted-foreground">{cat.label} · ₹{listing.price_amount.toLocaleString("en-IN")} · {listing.total_orders} orders</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-success/10 text-success border-success/20">Active</Badge>
                    </div>
                  </Card>
                );
              })
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Create Listing Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>List a Service</DialogTitle>
            <DialogDescription>Add your service to the AccouPro Marketplace</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Service Title *</label>
              <Input value={listingForm.title} onChange={(e) => setListingForm({ ...listingForm, title: e.target.value })} placeholder="e.g., Complete GST Filing Service" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Category *</label>
                <Select value={listingForm.category} onValueChange={(v) => setListingForm({ ...listingForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">City</label>
                <Select value={listingForm.city} onValueChange={(v) => setListingForm({ ...listingForm, city: v })}>
                  <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>
                    {CITIES.filter((c) => c !== "All Cities").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Description</label>
              <Textarea value={listingForm.description} onChange={(e) => setListingForm({ ...listingForm, description: e.target.value })} placeholder="Describe your service..." rows={3} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Features (comma-separated)</label>
              <Input value={listingForm.features} onChange={(e) => setListingForm({ ...listingForm, features: e.target.value })} placeholder="GSTR-1, GSTR-3B, Annual Returns" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Price (₹) *</label>
                <Input type="number" value={listingForm.price_amount} onChange={(e) => setListingForm({ ...listingForm, price_amount: e.target.value })} placeholder="2999" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Price Label</label>
                <Input value={listingForm.price_label} onChange={(e) => setListingForm({ ...listingForm, price_label: e.target.value })} placeholder="month, year, one-time" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Delivery Time</label>
                <Input value={listingForm.delivery_time} onChange={(e) => setListingForm({ ...listingForm, delivery_time: e.target.value })} placeholder="3-5 days" />
              </div>
            </div>
            <Button
              className="w-full"
              disabled={!listingForm.title || !listingForm.price_amount || createListing.isPending}
              onClick={() => createListing.mutate()}
            >
              <Zap className="h-4 w-4 mr-2" />
              {createListing.isPending ? "Publishing..." : "Publish Service"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedListing && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedListing.title}</DialogTitle>
                <DialogDescription>by {selectedListing.provider_name} · ₹{selectedListing.price_amount.toLocaleString("en-IN")}{selectedListing.price_label ? `/${selectedListing.price_label}` : ""}</DialogDescription>
              </DialogHeader>
              {selectedListing.description && (
                <p className="text-sm text-muted-foreground">{selectedListing.description}</p>
              )}
              {selectedListing.features.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-foreground">Includes:</p>
                  {selectedListing.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3 text-success flex-shrink-0" /> {f}
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-xs font-semibold text-foreground">Place Order</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Your Name *</label>
                    <Input value={orderForm.buyer_name} onChange={(e) => setOrderForm({ ...orderForm, buyer_name: e.target.value })} placeholder="Full name" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Company</label>
                    <Input value={orderForm.buyer_company} onChange={(e) => setOrderForm({ ...orderForm, buyer_company: e.target.value })} placeholder="Company name" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Email *</label>
                    <Input type="email" value={orderForm.buyer_email} onChange={(e) => setOrderForm({ ...orderForm, buyer_email: e.target.value })} placeholder="email@example.com" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Phone</label>
                    <Input value={orderForm.buyer_phone} onChange={(e) => setOrderForm({ ...orderForm, buyer_phone: e.target.value })} placeholder="+91 98765 43210" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Requirements</label>
                  <Textarea value={orderForm.requirements} onChange={(e) => setOrderForm({ ...orderForm, requirements: e.target.value })} placeholder="Any specific requirements..." rows={2} />
                </div>
                <Button
                  className="w-full"
                  disabled={!orderForm.buyer_name || !orderForm.buyer_email || placeOrder.isPending}
                  onClick={() => placeOrder.mutate()}
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  {placeOrder.isPending ? "Placing..." : `Purchase — ₹${selectedListing.price_amount.toLocaleString("en-IN")}`}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
